import { useCallback, useEffect, useState } from 'react';
import { Confirm, Loading, useDataProvider, useNotify } from 'react-admin';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { useCallbackPrompt } from '@app/hooks/useCallbackPrompt';
import { useErrorHandling } from '@app/hooks/useErrorHandling';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { Button } from '@mui/material';
import update from 'immutability-helper';

import { GenericObject } from '@app/components/common/Table/DefaultTable/types';

import { ACTION_MODE } from '../../ListTaxonomies';
import { TreeItem } from '../../TaxonomyTree/TreeItem';

interface TaxonomyReorderProps {
  selectedTaxonomy: GenericObject | null;
  handleChangeActionMode(actionMode: string): void;
}

export const TaxonomyReorder = ({ selectedTaxonomy, handleChangeActionMode }: TaxonomyReorderProps) => {
  const dataProvider = useDataProvider();
  const [handleError] = useErrorHandling();
  const notify = useNotify();

  const [taxonomies, setTaxonomies] = useState<GenericObject[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(isDirty);

  const fetchTaxonomies = useCallback(async () => {
    try {
      const { data } = await dataProvider.fetchSubTaxonomies('taxonomies', selectedTaxonomy?.id);
      setTaxonomies(
        data.sort((a: GenericObject, b: GenericObject) => {
          return (a.position as number) - (b.position as number);
        })
      );
    } catch (error: any) {
      handleError(error);
    }
  }, [dataProvider, selectedTaxonomy, handleError]);

  const moveCard = useCallback((dragIndex: number, hoverIndex: number) => {
    setIsDirty(true);
    setTaxonomies((prevTaxonomies) =>
      update(prevTaxonomies, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prevTaxonomies[dragIndex]],
        ],
      })
    );
  }, []);

  const onSaveOrder = async () => {
    if (!selectedTaxonomy) {
      return;
    }

    try {
      setIsLoading(true);
      const body = taxonomies.map((taxonomy) => taxonomy.id);
      await dataProvider.reorderSubTaxonomies('taxonomies', { id: selectedTaxonomy?.id, body });
      notify('Taxonomies have been reordered with success', { type: 'success' });
      handleChangeActionMode(ACTION_MODE.DEFAULT);
      setIsDirty(false);
    } catch (error: any) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  //Fetch taxonomies on mount
  useEffect(() => {
    selectedTaxonomy && fetchTaxonomies();
  }, [fetchTaxonomies, selectedTaxonomy]);

  if (isLoading) return <Loading />;

  return (
    <>
      <Confirm
        isOpen={showPrompt as boolean}
        title="Your changes haven't been saved"
        content="Are you sure you want to leave this page without saving?"
        onConfirm={confirmNavigation as () => void}
        cancel="Keep editing"
        confirm="Close without saving"
        onClose={cancelNavigation as () => void}
      />
      <DndProvider backend={HTML5Backend}>
        <Button
          style={{ marginLeft: '10px', marginBottom: '20px' }}
          variant="contained"
          onClick={onSaveOrder}
          startIcon={<SaveAltIcon />}>
          Save Order
        </Button>
        {taxonomies.map((taxonomy: GenericObject, index: number) => (
          <div
            key={taxonomy.id as number}
            style={{
              borderWidth: 0,
              borderStyle: 'solid',
              borderColor: 'rgba(0, 0, 0, 0.12)',
              borderBottomWidth: 'thin',
            }}>
            <TreeItem
              index={index}
              moveCard={moveCard}
              taxonomy={taxonomy}
              selectedTaxonomy={selectedTaxonomy}
              orderable
            />
          </div>
        ))}
      </DndProvider>
    </>
  );
};
